import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

import { BaseButton } from '../base/BaseButton';
import { BaseEmptyState } from '../base/BaseEmptyState';
import { BaseLoadingState } from '../base/BaseLoadingState';
import { BaseSystemState } from '../base/BaseSystemState';
import { cn, hasRenderableNode } from '../base/utils';
import {
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
  useMonkeysStatusStates,
} from '../../provider';
import {
  WorkbenchMasonryLayout,
  type WorkbenchMasonryItemSize,
  type WorkbenchMasonryLayoutContext,
} from './WorkbenchMasonryLayout';
import {
  resolveWorkbenchCollectionLoadingVariant,
  resolveWorkbenchCollectionSkeletonCapacity,
  WorkbenchCollectionSkeleton,
  type WorkbenchCollectionLoadingVariant,
  type WorkbenchCollectionSkeletonConfig,
} from './WorkbenchCollectionSkeleton';
import {
  useWorkbenchCollectionSelection,
  type WorkbenchCollectionKey,
  type WorkbenchCollectionSelectionBehavior,
  type WorkbenchCollectionSelectionMode,
} from './useWorkbenchCollectionSelection';

export type {
  WorkbenchCollectionLoadingVariant,
  WorkbenchCollectionSkeletonConfig,
} from './WorkbenchCollectionSkeleton';
export type {
  WorkbenchCollectionKey,
  WorkbenchCollectionSelectionBehavior,
  WorkbenchCollectionSelectionMode,
} from './useWorkbenchCollectionSelection';

export type WorkbenchCollectionLayoutMode = 'grid' | 'masonry' | 'dense' | 'rail';
export type WorkbenchCollectionActivationEvent = MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>;
export type WorkbenchCollectionItemStatus = 'idle' | 'loading' | 'pending' | 'failed' | string;
export type WorkbenchCollectionItemSlotName =
  | 'media'
  | 'fallback'
  | 'statusOverlay'
  | 'top'
  | 'selectionControl'
  | 'header'
  | 'title'
  | 'body'
  | 'meta'
  | 'badges'
  | 'actions'
  | 'footer'
  | 'detail'
  | 'detailTrigger';
export type WorkbenchCollectionResponsive<Value> = Value | {
  mobile?: Value;
  tablet?: Value;
  desktop?: Value;
};

export type WorkbenchCollectionItemSize = WorkbenchMasonryItemSize;

export interface WorkbenchCollectionLayoutContext {
  mode: WorkbenchCollectionLayoutMode;
  columnCount: number;
  columnSpan?: number;
  columnWidth: number;
  containerWidth: number;
  gap: number;
  itemWidth?: number;
  placementStrategy: 'shelf' | 'shortest' | 'dense';
  rowSpan?: number;
  rowHeight: number;
  size?: WorkbenchCollectionItemSize;
}

export interface WorkbenchCollectionLayoutSnapshot {
  mode: WorkbenchCollectionLayoutMode;
  columnCount: number;
  columnWidth: number;
  containerWidth: number;
  gap: number;
  itemCount: number;
}

export interface WorkbenchCollectionItemContext<Item> {
  item: Item;
  index: number;
  key: WorkbenchCollectionKey;
  status: WorkbenchCollectionItemStatus;
  disabled: boolean;
  selected: boolean;
  selectionMode: WorkbenchCollectionSelectionMode;
  layout: WorkbenchCollectionLayoutContext;
  select: (behavior?: WorkbenchCollectionSelectionBehavior) => void;
  selectFromEvent: (
    event: Pick<WorkbenchCollectionActivationEvent, 'shiftKey'>,
    behavior?: WorkbenchCollectionSelectionBehavior,
  ) => void;
  preview: () => void;
  detail: () => void;
  renderSlot: (name: WorkbenchCollectionItemSlotName) => ReactNode;
  notifyMediaLoad: () => void;
  notifyMediaError: (error?: unknown) => void;
}

export interface WorkbenchCollectionStateConfig {
  loading?: boolean;
  loadingMore?: boolean;
  error?: unknown;
  empty?: boolean;
  hasMore?: boolean;
  permission?: boolean;
  offline?: boolean;
}

export interface WorkbenchCollectionSelectionConfig<Item> {
  mode?: WorkbenchCollectionSelectionMode;
  selectedKeys?: Iterable<WorkbenchCollectionKey>;
  defaultSelectedKeys?: Iterable<WorkbenchCollectionKey>;
  behavior?: WorkbenchCollectionSelectionBehavior;
  isItemDisabled?: (item: Item, index: number) => boolean;
}

export interface WorkbenchCollectionGroupingConfig<Item> {
  getGroupKey: (item: Item, index: number) => WorkbenchCollectionKey;
  getGroupLabel?: (groupKey: WorkbenchCollectionKey, firstItem: Item) => ReactNode;
}

export interface WorkbenchCollectionLoadingConfig {
  rootMargin?: string;
  threshold?: number;
  /** Request another page while the configured scroll viewport is not filled. */
  autoFill?: boolean;
  /** Collection loading defaults to a layout-aware skeleton. */
  variant?: WorkbenchCollectionLoadingVariant;
  skeleton?: WorkbenchCollectionSkeletonConfig;
}

export type WorkbenchCollectionHoverEffect =
  | 'none'
  | 'highlight'
  | 'lift'
  | 'scale'
  | 'shadow'
  | 'lift-shadow';

export type WorkbenchCollectionTitleVisibility = 'always' | 'hover' | 'never';
export type WorkbenchCollectionTitlePlacement =
  | 'above'
  | 'below'
  | 'overlay-top'
  | 'overlay-center'
  | 'overlay-bottom';
export type WorkbenchCollectionActionsVisibility = 'always' | 'hover' | 'never';
export type WorkbenchCollectionActionsPlacement =
  | 'above'
  | 'below'
  | 'overlay-top'
  | 'overlay-bottom';
export type WorkbenchCollectionItemAlign = 'start' | 'center' | 'end';

export interface WorkbenchCollectionItemTitleConfig<Item> {
  getContent?: (item: Item, context: WorkbenchCollectionItemContext<Item>) => ReactNode;
  visibility?: WorkbenchCollectionTitleVisibility;
  placement?: WorkbenchCollectionTitlePlacement;
  align?: WorkbenchCollectionItemAlign;
  maxLines?: number;
  surface?: 'none' | 'surface' | 'scrim';
}

export interface WorkbenchCollectionItemActionsConfig {
  visibility?: WorkbenchCollectionActionsVisibility;
  placement?: WorkbenchCollectionActionsPlacement;
  align?: WorkbenchCollectionItemAlign;
  wrap?: boolean;
}

export interface WorkbenchCollectionItemConfig<Item> {
  title?: WorkbenchCollectionItemTitleConfig<Item>;
  actions?: WorkbenchCollectionItemActionsConfig;
}

export interface WorkbenchCollectionAppearanceConfig {
  density?: 'compact' | 'comfortable' | 'detailed' | string;
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | string;
  mediaFit?: 'cover' | 'contain' | 'natural';
  hover?: WorkbenchCollectionHoverEffect;
  selection?: 'none' | 'ring' | 'outline';
}

export interface WorkbenchCollectionLabels {
  collection: string;
  loading: string;
  empty: string;
  loadError: string;
  refreshError: string;
  retry: string;
  loadingMore: string;
  permission: string;
  offline: string;
  requestAccess: string;
}

export interface WorkbenchCollectionVirtualizationRenderOptions<Item> {
  items: readonly Item[];
  keys: readonly WorkbenchCollectionKey[];
  layout: WorkbenchCollectionLayoutConfig<Item>;
  scrollOwner: HTMLElement | null;
  renderItem: (item: Item, index: number, layout: WorkbenchCollectionLayoutContext) => ReactNode;
}

export interface WorkbenchCollectionVirtualizationAdapter<Item> {
  supports?: (mode: WorkbenchCollectionLayoutMode) => boolean;
  render: (options: WorkbenchCollectionVirtualizationRenderOptions<Item>) => ReactNode;
}

export interface WorkbenchCollectionVirtualizationConfig<Item> {
  enabled?: boolean;
  adapter?: WorkbenchCollectionVirtualizationAdapter<Item>;
  fallback?: 'render-all' | 'error';
}

export interface WorkbenchCollectionLayoutConfig<Item> {
  mode: WorkbenchCollectionLayoutMode;
  columns?: WorkbenchCollectionResponsive<number> | 'auto';
  /** Control whether an underfilled automatic grid stretches its items or preserves full-grid card widths. */
  underfilledBehavior?: 'stretch-items' | 'preserve-columns';
  maxColumns?: WorkbenchCollectionResponsive<number>;
  minCardWidth?: number | string;
  idealCardWidth?: number | string;
  maxCardWidth?: number | string;
  cardWidth?: number | string;
  columnWidth?: number | string;
  gap?: number;
  rowHeight?: number;
  placementStrategy?: 'shelf' | 'shortest' | 'dense';
  padding?: number | string;
  fillHeight?: boolean;
  maxHeight?: number | string;
  scrollOwner?: 'self' | 'parent' | HTMLElement | null;
  getItemSize?: (
    item: Item,
    context: WorkbenchCollectionLayoutContext,
  ) => WorkbenchCollectionItemSize | undefined;
  getItemAspectRatio?: (item: Item, index: number) => number | undefined;
}

export interface WorkbenchCollectionConfig<Item> {
  getItemKey?: (item: Item, index: number) => WorkbenchCollectionKey;
  getItemStatus?: (item: Item, index: number) => WorkbenchCollectionItemStatus;
  layout: WorkbenchCollectionLayoutConfig<Item>;
  state?: WorkbenchCollectionStateConfig;
  selection?: WorkbenchCollectionSelectionConfig<Item>;
  grouping?: WorkbenchCollectionGroupingConfig<Item>;
  loading?: WorkbenchCollectionLoadingConfig;
  virtualization?: WorkbenchCollectionVirtualizationConfig<Item>;
  item?: WorkbenchCollectionItemConfig<Item>;
  appearance?: WorkbenchCollectionAppearanceConfig;
  labels?: Partial<WorkbenchCollectionLabels>;
  scrollRestorationKey?: string;
}

export interface WorkbenchCollectionSlotContext<Item> {
  items: readonly Item[];
  error?: unknown;
  loading: boolean;
  loadingMore: boolean;
  selectedKeys: ReadonlySet<WorkbenchCollectionKey>;
  retry: () => void;
}

export interface WorkbenchCollectionGroupContext<Item> {
  key: WorkbenchCollectionKey;
  label?: ReactNode;
  items: readonly Item[];
  groupIndex: number;
}

export type WorkbenchCollectionItemSlots<Item> = Partial<Record<
  WorkbenchCollectionItemSlotName,
  (item: Item, context: WorkbenchCollectionItemContext<Item>) => ReactNode
>>;

export interface WorkbenchCollectionSlots<Item> {
  header?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  toolbar?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  beforeItems?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  groupHeading?: (
    group: WorkbenchCollectionGroupContext<Item>,
    context: WorkbenchCollectionSlotContext<Item>,
  ) => ReactNode;
  selectionToolbar?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  loading?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  empty?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  error?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  permission?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  offline?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  loadMore?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  footer?: ReactNode | ((context: WorkbenchCollectionSlotContext<Item>) => ReactNode);
  item?: WorkbenchCollectionItemSlots<Item>;
}

export interface WorkbenchCollectionCallbacks<Item> {
  onItemClick?: (item: Item, context: WorkbenchCollectionItemContext<Item>, event: WorkbenchCollectionActivationEvent) => void;
  onItemDoubleClick?: (item: Item, context: WorkbenchCollectionItemContext<Item>, event: MouseEvent<HTMLElement>) => void;
  onItemHoverChange?: (
    item: Item,
    context: WorkbenchCollectionItemContext<Item>,
    hovered: boolean,
    event: MouseEvent<HTMLElement>,
  ) => void;
  onItemPreview?: (item: Item, context: WorkbenchCollectionItemContext<Item>) => void;
  onItemDetail?: (item: Item, context: WorkbenchCollectionItemContext<Item>) => void;
  onItemDragStart?: (item: Item, context: WorkbenchCollectionItemContext<Item>, event: DragEvent<HTMLElement>) => void;
  onItemDrop?: (item: Item, context: WorkbenchCollectionItemContext<Item>, event: DragEvent<HTMLElement>) => void;
  onSelectionChange?: (
    selectedKeys: ReadonlySet<WorkbenchCollectionKey>,
    details: { item: Item; key: WorkbenchCollectionKey; behavior: WorkbenchCollectionSelectionBehavior },
  ) => void;
  onLoadMore?: () => void | Promise<void>;
  onRetry?: () => void;
  onRequestAccess?: () => void;
  onLayoutChange?: (snapshot: WorkbenchCollectionLayoutSnapshot) => void;
  onLayoutReady?: (snapshot: WorkbenchCollectionLayoutSnapshot) => void;
  onMediaLoad?: (item: Item, context: WorkbenchCollectionItemContext<Item>) => void;
  onMediaError?: (item: Item, context: WorkbenchCollectionItemContext<Item>, error?: unknown) => void;
}

export interface WorkbenchCollectionClassNames {
  root?: string;
  header?: string;
  toolbar?: string;
  selectionToolbar?: string;
  beforeItems?: string;
  items?: string;
  layout?: string;
  group?: string;
  groupHeading?: string;
  item?: string;
  itemContent?: string;
  itemTitle?: string;
  itemActions?: string;
  status?: string;
  loadMore?: string;
  footer?: string;
}

export interface WorkbenchCollectionProps<Item> {
  items: readonly Item[];
  config: WorkbenchCollectionConfig<Item>;
  renderItem: (item: Item, context: WorkbenchCollectionItemContext<Item>) => ReactNode;
  slots?: WorkbenchCollectionSlots<Item>;
  callbacks?: WorkbenchCollectionCallbacks<Item>;
  className?: string;
  classNames?: WorkbenchCollectionClassNames;
  style?: CSSProperties;
  ariaLabel?: string;
}

interface NormalizedCollectionItem<Item> {
  item: Item;
  index: number;
  key: WorkbenchCollectionKey;
  stringKey: string;
}

interface NormalizedCollectionGroup<Item> {
  key: WorkbenchCollectionKey;
  stringKey: string;
  label?: ReactNode;
  items: NormalizedCollectionItem<Item>[];
}

const DEFAULT_GAP_PX = 12;
const DEFAULT_ROW_HEIGHT_PX = 8;
const DEFAULT_CARD_WIDTH_PX = 280;
const BREAKPOINT_TABLET_PX = 640;
const BREAKPOINT_DESKTOP_PX = 1024;
const MAX_COLUMN_COUNT = 24;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function resolveRenderableSlot<Context>(
  slot: ReactNode | ((context: Context) => ReactNode) | undefined,
  context: Context,
) {
  return typeof slot === 'function' ? slot(context) : slot;
}

function resolveLengthToPixels(value: number | string | undefined, fallback: number, node: Element | null) {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value !== 'string') return fallback;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return fallback;
  if (/^\d+(\.\d+)?(px)?$/.test(normalized)) {
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }
  if (normalized.endsWith('rem')) {
    const parsed = Number.parseFloat(normalized);
    const rootFontSize = typeof window === 'undefined'
      ? 16
      : Number.parseFloat(window.getComputedStyle(node || document.documentElement).fontSize) || 16;
    return Number.isFinite(parsed) && parsed > 0 ? parsed * rootFontSize : fallback;
  }
  return fallback;
}

function normalizeColumns(value: unknown) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined;
  return clamp(Math.round(value), 1, MAX_COLUMN_COUNT);
}

function resolveResponsiveColumns(
  columns: WorkbenchCollectionResponsive<number> | 'auto' | undefined,
  containerWidth: number,
) {
  if (columns === 'auto' || columns === undefined) return undefined;
  if (typeof columns === 'number') return normalizeColumns(columns);
  const mobile = normalizeColumns(columns.mobile);
  const tablet = normalizeColumns(columns.tablet);
  const desktop = normalizeColumns(columns.desktop);
  if (containerWidth >= BREAKPOINT_DESKTOP_PX) return desktop ?? tablet ?? mobile;
  if (containerWidth >= BREAKPOINT_TABLET_PX) return tablet ?? mobile ?? desktop;
  return mobile ?? tablet ?? desktop;
}

function resolveColumnCount({
  containerWidth,
  itemCount,
  gap,
  columns,
  underfilledBehavior,
  maxColumns,
  minCardWidth,
  idealCardWidth,
  maxCardWidth,
  node,
}: {
  containerWidth: number;
  itemCount: number;
  gap: number;
  columns: WorkbenchCollectionResponsive<number> | 'auto' | undefined;
  underfilledBehavior?: 'stretch-items' | 'preserve-columns';
  maxColumns?: WorkbenchCollectionResponsive<number>;
  minCardWidth?: number | string;
  idealCardWidth?: number | string;
  maxCardWidth?: number | string;
  node: Element | null;
}) {
  const fixedColumns = resolveResponsiveColumns(columns, containerWidth);
  const resolvedMaxColumns = resolveResponsiveColumns(maxColumns, containerWidth);
  if (fixedColumns) return resolvedMaxColumns ? Math.min(fixedColumns, resolvedMaxColumns) : fixedColumns;
  if (!containerWidth) return 1;

  const idealWidth = resolveLengthToPixels(idealCardWidth, DEFAULT_CARD_WIDTH_PX, node);
  const minWidth = resolveLengthToPixels(minCardWidth, idealWidth * 0.78, node);
  const maxWidth = Math.max(
    minWidth,
    resolveLengthToPixels(maxCardWidth, idealWidth * 1.35, node),
  );
  const safeItemCount = Math.max(itemCount, 1);
  const maxCandidate = Math.max(
    1,
    Math.min(
      underfilledBehavior === 'preserve-columns' ? MAX_COLUMN_COUNT : safeItemCount,
      Math.ceil((containerWidth + gap) / (Math.max(minWidth * 0.75, 1) + gap)),
    ),
  );

  let bestCount = 1;
  let bestScore = Number.POSITIVE_INFINITY;
  for (let candidate = 1; candidate <= maxCandidate; candidate += 1) {
    const width = (containerWidth - gap * Math.max(candidate - 1, 0)) / candidate;
    if (!Number.isFinite(width) || width <= 0) continue;
    const score = Math.abs(width - idealWidth)
      + Math.max(0, minWidth - width) * 3
      + Math.max(0, width - maxWidth) * 2;
    if (score < bestScore) {
      bestScore = score;
      bestCount = candidate;
    }
  }
  return resolvedMaxColumns ? Math.min(bestCount, resolvedMaxColumns) : bestCount;
}

function normalizeGap(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : DEFAULT_GAP_PX;
}

function normalizeRowHeight(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : DEFAULT_ROW_HEIGHT_PX;
}

function normalizeThreshold(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value)
    ? clamp(value, 0, 1)
    : 0;
}

function normalizeItemSize(
  size: WorkbenchCollectionItemSize | undefined,
  aspectRatio: number | undefined,
  columnWidth: number,
) {
  const normalizedAspectRatio = Number.isFinite(aspectRatio) && Number(aspectRatio) > 0
    ? Number(aspectRatio)
    : undefined;
  const normalizedHeight = size?.height ?? (
    normalizedAspectRatio ? Math.max(1, columnWidth / normalizedAspectRatio) : undefined
  );
  return {
    ...size,
    height: normalizedHeight,
  };
}

function getDefaultItemKey<Item>(item: Item, index: number): WorkbenchCollectionKey {
  if (item && typeof item === 'object') {
    const candidate = item as Record<string, unknown>;
    if (typeof candidate.id === 'string' || typeof candidate.id === 'number') return candidate.id;
    if (typeof candidate.key === 'string' || typeof candidate.key === 'number') return candidate.key;
  }
  return index;
}

function getScrollStorageKey(key: string) {
  return `@inf-monkeys-tech/monkeys-design:workbench-collection:${key}`;
}

function getParentScrollOwner(node: HTMLElement | null) {
  let current = node?.parentElement ?? null;
  while (current) {
    if (typeof window === 'undefined') return current;
    const overflowY = window.getComputedStyle(current).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return current;
    current = current.parentElement;
  }
  return null;
}

function isInteractiveEventTarget(target: EventTarget | null, currentTarget: HTMLElement) {
  if (typeof Element === 'undefined' || !(target instanceof Element) || target === currentTarget) return false;
  const control = target.closest(
    'a, button, input, select, textarea, summary, [contenteditable="true"], [data-collection-action]',
  );
  return Boolean(control && currentTarget.contains(control));
}

function normalizeTitleMaxLines(value: number | undefined) {
  if (!Number.isFinite(value) || Number(value) <= 0) return undefined;
  return clamp(Math.round(Number(value)), 1, 12);
}

export function WorkbenchCollection<Item>({
  items,
  config,
  renderItem,
  slots,
  callbacks,
  className,
  classNames,
  style,
  ariaLabel,
}: WorkbenchCollectionProps<Item>) {
  const componentAttributes = useMonkeysComponentAttributes();
  const localeMessages = useMonkeysLocaleMessages().workbench.collection;
  const statusStates = useMonkeysStatusStates();
  const componentId = useId().replace(/:/g, '');
  const rootRef = useRef<HTMLElement | null>(null);
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRequestPendingRef = useRef(false);
  const loadMoreIntersectionActiveRef = useRef(false);
  const loadMoreIntersectionConsumedRef = useRef(false);
  const loadMoreScrollValidationPendingRef = useRef(false);
  const observedLoadMoreTargetRef = useRef<HTMLDivElement | null>(null);
  const layoutReadyRef = useRef(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemsNode, setItemsNode] = useState<HTMLDivElement | null>(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const registerItemsNode = useCallback((node: HTMLDivElement | null) => {
    itemsRef.current = node;
    setItemsNode(node);
  }, []);

  const mode = config.layout.mode;
  if (mode !== 'grid' && mode !== 'masonry' && mode !== 'dense' && mode !== 'rail') {
    throw new TypeError(`WorkbenchCollection layout.mode "${String(mode)}" is not supported.`);
  }
  const gap = normalizeGap(config.layout.gap);
  const rowHeight = normalizeRowHeight(config.layout.rowHeight);
  const getItemKey = config.getItemKey ?? getDefaultItemKey;
  const normalizedItems = useMemo<NormalizedCollectionItem<Item>[]>(() => (
    items.map((item, index) => {
      const key = getItemKey(item, index);
      return { item, index, key, stringKey: String(key) };
    })
  ), [getItemKey, items]);
  const keysSignature = normalizedItems.map(({ stringKey }) => stringKey).join('\u0001');
  const state = config.state;
  const loading = state?.loading === true;
  const loadingMore = state?.loadingMore === true;
  const hasMore = state?.hasMore === true;
  const loadingVariant = resolveWorkbenchCollectionLoadingVariant(config.loading?.variant);
  const layoutItemCount = loading && normalizedItems.length === 0 && loadingVariant === 'skeleton'
    ? resolveWorkbenchCollectionSkeletonCapacity(config.loading?.skeleton)
    : normalizedItems.length;

  const selectionMode = config.selection?.mode ?? 'none';
  const isSelectionItemDisabled = useCallback(
    (entry: NormalizedCollectionItem<Item>) => (
      config.selection?.isItemDisabled?.(entry.item, entry.index) ?? false
    ),
    [config.selection?.isItemDisabled],
  );
  const getSelectionItemKey = useCallback(
    (entry: NormalizedCollectionItem<Item>) => entry.key,
    [],
  );
  const handleSelectionChange = useCallback((
    next: ReadonlySet<WorkbenchCollectionKey>,
    details: {
      item: NormalizedCollectionItem<Item>;
      key: WorkbenchCollectionKey;
      behavior: WorkbenchCollectionSelectionBehavior;
    },
  ) => {
    callbacksRef.current?.onSelectionChange?.(next, {
      item: details.item.item,
      key: details.key,
      behavior: details.behavior,
    });
  }, []);
  const { selectedKeys, selectKey } = useWorkbenchCollectionSelection({
    items: normalizedItems,
    getItemKey: getSelectionItemKey,
    mode: selectionMode,
    behavior: config.selection?.behavior,
    selectedKeys: config.selection?.selectedKeys,
    defaultSelectedKeys: config.selection?.defaultSelectedKeys,
    isItemDisabled: isSelectionItemDisabled,
    onSelectionChange: handleSelectionChange,
  });

  useEffect(() => {
    const node = itemsNode;
    if (!node) return undefined;
    const updateWidth = () => {
      const nextWidth = Math.round(node.getBoundingClientRect().width);
      setContainerWidth((previous) => previous === nextWidth ? previous : nextWidth);
    };
    updateWidth();
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(node);
      return () => observer.disconnect();
    }
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [itemsNode]);

  const columnCount = resolveColumnCount({
    containerWidth,
    itemCount: layoutItemCount,
    gap,
    columns: config.layout.columns,
    underfilledBehavior: config.layout.underfilledBehavior,
    maxColumns: config.layout.maxColumns,
    minCardWidth: config.layout.minCardWidth,
    idealCardWidth: config.layout.idealCardWidth ?? config.layout.columnWidth,
    maxCardWidth: config.layout.maxCardWidth,
    node: itemsRef.current,
  });
  const columnWidth = containerWidth > 0
    ? Math.max(0, (containerWidth - gap * Math.max(columnCount - 1, 0)) / columnCount)
    : resolveLengthToPixels(
        config.layout.cardWidth ?? config.layout.columnWidth ?? config.layout.idealCardWidth,
        DEFAULT_CARD_WIDTH_PX,
        itemsRef.current,
      );
  const placementStrategy = config.layout.placementStrategy ?? (
    mode === 'dense' ? 'dense' : mode === 'masonry' ? 'shortest' : 'shelf'
  );
  const labels = useMemo<WorkbenchCollectionLabels>(() => ({
    collection: config.labels?.collection ?? localeMessages.label,
    loading: config.labels?.loading ?? localeMessages.loading,
    empty: config.labels?.empty ?? localeMessages.empty,
    loadError: config.labels?.loadError ?? localeMessages.loadError,
    refreshError: config.labels?.refreshError ?? localeMessages.refreshError,
    retry: config.labels?.retry ?? localeMessages.retry,
    loadingMore: config.labels?.loadingMore ?? localeMessages.loadingMore,
    permission: config.labels?.permission ?? localeMessages.permission,
    offline: config.labels?.offline ?? localeMessages.offline,
    requestAccess: config.labels?.requestAccess ?? localeMessages.requestAccess,
  }), [config.labels, localeMessages]);

  const virtualizationError = useMemo(() => {
    const virtualization = config.virtualization;
    if (virtualization?.enabled !== true || virtualization.fallback !== 'error') return null;
    if (!virtualization.adapter) {
      return new Error('WorkbenchCollection virtualization requires an adapter.');
    }
    if (virtualization.adapter.supports?.(mode) === false) {
      return new Error(`WorkbenchCollection virtualization adapter does not support mode "${mode}".`);
    }
    return null;
  }, [config.virtualization, mode]);

  const layoutSnapshot = useMemo<WorkbenchCollectionLayoutSnapshot>(() => ({
    mode,
    columnCount,
    columnWidth,
    containerWidth,
    gap,
    itemCount: normalizedItems.length,
  }), [columnCount, columnWidth, containerWidth, gap, mode, normalizedItems.length]);

  useEffect(() => {
    callbacksRef.current?.onLayoutChange?.(layoutSnapshot);
    if (containerWidth > 0 && !layoutReadyRef.current) {
      layoutReadyRef.current = true;
      callbacksRef.current?.onLayoutReady?.(layoutSnapshot);
    }
  }, [layoutSnapshot, containerWidth]);

  useEffect(() => {
    layoutReadyRef.current = false;
  }, [mode]);

  const resolveScrollOwner = useCallback(() => {
    const requestedOwner = config.layout.scrollOwner;
    if (requestedOwner && requestedOwner !== 'self' && requestedOwner !== 'parent') return requestedOwner;
    if (requestedOwner === 'parent') return getParentScrollOwner(rootRef.current);
    return rootRef.current;
  }, [config.layout.scrollOwner]);

  useEffect(() => {
    const restorationKey = config.scrollRestorationKey?.trim();
    if (!restorationKey || typeof window === 'undefined') return undefined;
    const owner = resolveScrollOwner();
    if (!owner) return undefined;
    const storageKey = getScrollStorageKey(restorationKey);
    try {
      const stored = Number.parseFloat(window.sessionStorage.getItem(storageKey) ?? '');
      if (Number.isFinite(stored) && stored >= 0) owner.scrollTop = stored;
    } catch {
      // Storage can be unavailable in sandboxed or privacy-restricted contexts.
    }
    const saveScrollPosition = () => {
      try {
        window.sessionStorage.setItem(storageKey, String(owner.scrollTop));
      } catch {
        // Scroll restoration is optional and must never break collection rendering.
      }
    };
    owner.addEventListener('scroll', saveScrollPosition, { passive: true });
    return () => {
      saveScrollPosition();
      owner.removeEventListener('scroll', saveScrollPosition);
    };
  }, [config.scrollRestorationKey, resolveScrollOwner]);

  const canLoadMore = typeof callbacks?.onLoadMore === 'function';
  const effectiveError = state?.error ?? virtualizationError;

  useEffect(() => {
    if (!canLoadMore || !hasMore) {
      loadMoreIntersectionActiveRef.current = false;
      loadMoreIntersectionConsumedRef.current = false;
      loadMoreScrollValidationPendingRef.current = false;
    }
  }, [canLoadMore, hasMore]);

  const requestLoadMore = useCallback(() => {
    if (!canLoadMore || !hasMore || loadingMore || loadMoreRequestPendingRef.current) return;
    loadMoreRequestPendingRef.current = true;
    const releaseRequest = () => {
      loadMoreRequestPendingRef.current = false;
    };
    try {
      const result = callbacksRef.current?.onLoadMore?.();
      if (result && typeof result.then === 'function') {
        void result.then(releaseRequest, releaseRequest);
      } else {
        releaseRequest();
      }
    } catch (error) {
      releaseRequest();
      throw error;
    }
  }, [canLoadMore, hasMore, loadingMore]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (observedLoadMoreTargetRef.current !== sentinel) {
      observedLoadMoreTargetRef.current = sentinel;
      loadMoreIntersectionActiveRef.current = false;
      loadMoreIntersectionConsumedRef.current = false;
      loadMoreScrollValidationPendingRef.current = false;
    }
    if (!sentinel || !canLoadMore || !hasMore) return undefined;
    if (typeof IntersectionObserver !== 'function') return undefined;
    const owner = resolveScrollOwner();
    let observerHasDelivered = false;
    const observer = new IntersectionObserver((entries) => {
      const isIntersecting = entries.some((entry) => entry.isIntersecting);
      if (loadMoreScrollValidationPendingRef.current) {
        observerHasDelivered = true;
        loadMoreScrollValidationPendingRef.current = false;
        loadMoreIntersectionActiveRef.current = isIntersecting;
        if (!isIntersecting) {
          loadMoreIntersectionConsumedRef.current = false;
          return;
        }
        if (loadMoreIntersectionConsumedRef.current) return;
        loadMoreIntersectionConsumedRef.current = true;
        requestLoadMore();
        return;
      }
      if (!observerHasDelivered) {
        observerHasDelivered = true;
        loadMoreIntersectionActiveRef.current = isIntersecting;
        if (!isIntersecting) loadMoreIntersectionConsumedRef.current = false;
        return;
      }
      if (!isIntersecting) {
        loadMoreIntersectionActiveRef.current = false;
        loadMoreIntersectionConsumedRef.current = false;
        return;
      }
      if (loadMoreIntersectionActiveRef.current) return;
      loadMoreIntersectionActiveRef.current = true;
      loadMoreIntersectionConsumedRef.current = true;
      requestLoadMore();
    }, {
      root: owner === rootRef.current ? owner : owner || null,
      rootMargin: config.loading?.rootMargin ?? '240px',
      threshold: normalizeThreshold(config.loading?.threshold),
    });
    const scrollTarget = owner ?? (typeof window === 'undefined' ? null : window);
    const validateActiveIntersection = () => {
      if (
        !loadMoreIntersectionActiveRef.current
        || loadMoreIntersectionConsumedRef.current
        || loadMoreScrollValidationPendingRef.current
      ) return;
      loadMoreScrollValidationPendingRef.current = true;
      observer.unobserve(sentinel);
      observer.takeRecords();
      observer.observe(sentinel);
    };
    observer.observe(sentinel);
    scrollTarget?.addEventListener('scroll', validateActiveIntersection, { passive: true });
    return () => {
      observer.disconnect();
      scrollTarget?.removeEventListener('scroll', validateActiveIntersection);
    };
  }, [canLoadMore, config.loading?.rootMargin, config.loading?.threshold, hasMore, keysSignature, loadingMore, normalizedItems.length, requestLoadMore, resolveScrollOwner]);

  useEffect(() => {
    if (!config.loading?.autoFill || !canLoadMore || !hasMore || loadingMore) return;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;
    const owner = resolveScrollOwner()
      ?? (config.layout.scrollOwner === 'parent' ? rootRef.current?.parentElement ?? null : null)
      ?? rootRef.current;
    if (!owner || owner.clientHeight <= 0) return;
    if (owner.scrollHeight <= owner.clientHeight + 1) {
      loadMoreIntersectionConsumedRef.current = true;
      requestLoadMore();
    }
  }, [
    canLoadMore,
    config.layout.scrollOwner,
    config.loading?.autoFill,
    hasMore,
    keysSignature,
    loadingMore,
    normalizedItems.length,
    requestLoadMore,
    resolveScrollOwner,
  ]);

  const groups = useMemo<NormalizedCollectionGroup<Item>[]>(() => {
    if (!config.grouping) {
      return [{ key: '__all__', stringKey: '__all__', items: normalizedItems }];
    }
    const grouped = new Map<string, NormalizedCollectionGroup<Item>>();
    normalizedItems.forEach((entry) => {
      const groupKey = config.grouping?.getGroupKey(entry.item, entry.index) ?? '__all__';
      const stringKey = String(groupKey);
      const existing = grouped.get(stringKey);
      if (existing) {
        existing.items.push(entry);
        return;
      }
      grouped.set(stringKey, {
        key: groupKey,
        stringKey,
        label: config.grouping?.getGroupLabel?.(groupKey, entry.item),
        items: [entry],
      });
    });
    return Array.from(grouped.values());
  }, [config.grouping, normalizedItems]);

  const createItemContext = useCallback((
    entry: NormalizedCollectionItem<Item>,
    layout: WorkbenchCollectionLayoutContext,
  ): WorkbenchCollectionItemContext<Item> => {
    const disabled = config.selection?.isItemDisabled?.(entry.item, entry.index) ?? false;
    const context: WorkbenchCollectionItemContext<Item> = {
      item: entry.item,
      index: entry.index,
      key: entry.key,
      status: config.getItemStatus?.(entry.item, entry.index) ?? 'idle',
      disabled,
      selected: selectedKeys.has(entry.key),
      selectionMode,
      layout,
      select: (behavior) => selectKey(entry.key, { behavior }),
      selectFromEvent: (event, behavior) => selectKey(entry.key, {
        behavior,
        shiftKey: event.shiftKey,
      }),
      preview: () => callbacks?.onItemPreview?.(entry.item, context),
      detail: () => callbacks?.onItemDetail?.(entry.item, context),
      renderSlot: (name) => {
        return slots?.item?.[name]?.(entry.item, context);
      },
      notifyMediaLoad: () => callbacks?.onMediaLoad?.(entry.item, context),
      notifyMediaError: (error) => callbacks?.onMediaError?.(entry.item, context, error),
    };
    return context;
  }, [callbacks, config.getItemStatus, config.selection, selectedKeys, selectKey, selectionMode, slots?.item]);

  const getItemInteractionProps = useCallback((
    entry: NormalizedCollectionItem<Item>,
    context: WorkbenchCollectionItemContext<Item>,
  ): HTMLAttributes<HTMLDivElement> & Record<`data-${string}`, string | boolean | undefined> => {
    const handleKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      if (selectionMode !== 'none') context.selectFromEvent(event);
      else callbacks?.onItemClick?.(entry.item, context, event);
    };
    return {
      id: `${componentId}-item-${entry.index}`,
      'data-monkeys-collection-item': '',
      'data-item-key': entry.stringKey,
      'data-selected': context.selected || undefined,
      'data-disabled': context.disabled || undefined,
      'data-status': context.status,
      role: selectionMode === 'none' ? 'listitem' : 'option',
      'aria-selected': selectionMode === 'none' ? undefined : context.selected,
      'aria-disabled': context.disabled || undefined,
      tabIndex:
        callbacks?.onItemClick || selectionMode !== 'none' || config.item?.title?.visibility === 'hover' ? 0 : undefined,
      draggable: callbacks?.onItemDragStart ? true : undefined,
      className: cn(
        'min-w-0 group/workbench-collection-item outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        config.appearance?.hover && config.appearance.hover !== 'none'
          && 'transition-[transform,box-shadow,background-color] duration-200',
        config.appearance?.hover === 'highlight' && 'hover:bg-muted/30',
        config.appearance?.hover === 'lift' && 'hover:-translate-y-0.5 motion-reduce:transform-none',
        config.appearance?.hover === 'scale' && 'hover:scale-[1.01] motion-reduce:transform-none',
        config.appearance?.hover === 'shadow' && 'hover:shadow-md',
        config.appearance?.hover === 'lift-shadow'
          && 'hover:-translate-y-0.5 hover:shadow-md motion-reduce:transform-none',
        config.appearance?.selection === 'ring' && context.selected && 'ring-2 ring-primary',
        config.appearance?.selection === 'outline' && context.selected && 'outline outline-2 outline-primary',
        classNames?.item,
      ),
      onClick: (event) => {
        if (isInteractiveEventTarget(event.target, event.currentTarget)) return;
        if (selectionMode !== 'none') context.selectFromEvent(event);
        callbacks?.onItemClick?.(entry.item, context, event);
      },
      onDoubleClick: (event) => {
        if (isInteractiveEventTarget(event.target, event.currentTarget)) return;
        callbacks?.onItemDoubleClick?.(entry.item, context, event);
      },
      onMouseEnter: (event) => callbacks?.onItemHoverChange?.(entry.item, context, true, event),
      onMouseLeave: (event) => callbacks?.onItemHoverChange?.(entry.item, context, false, event),
      onKeyDown: handleKeyboard,
      onDragStart: (event) => callbacks?.onItemDragStart?.(entry.item, context, event),
      onDragOver: callbacks?.onItemDrop ? (event) => event.preventDefault() : undefined,
      onDrop: (event) => callbacks?.onItemDrop?.(entry.item, context, event),
    };
  }, [callbacks, classNames?.item, componentId, config.appearance, config.item?.title?.visibility, selectionMode]);

  const renderItemContent = useCallback((
    entry: NormalizedCollectionItem<Item>,
    context: WorkbenchCollectionItemContext<Item>,
  ) => {
    const content = renderItem(entry.item, context);
    const titleConfig = config.item?.title;
    const visibility = titleConfig?.visibility ?? 'always';
    const title = titleConfig && visibility !== 'never'
      ? titleConfig.getContent?.(entry.item, context) ?? context.renderSlot('title')
      : null;
    const hasTitle = Boolean(titleConfig && hasRenderableNode(title));
    const placement = titleConfig?.placement ?? 'below';
    const titleOverlay = hasTitle && placement.startsWith('overlay-');
    const maxLines = normalizeTitleMaxLines(titleConfig?.maxLines);
    const surface = titleConfig?.surface ?? (titleOverlay ? 'scrim' : 'none');
    const titleNode = hasTitle ? (
      <div
        data-collection-item-title=""
        data-title-placement={placement}
        data-title-visibility={visibility}
        className={cn(
          'min-w-0 px-1 py-2 text-sm font-medium text-foreground',
          titleConfig?.align === 'center' && 'text-center',
          titleConfig?.align === 'end' && 'text-right',
          visibility === 'hover'
            && '!opacity-0 transition-opacity group-hover/workbench-collection-item:!opacity-100 group-focus-within/workbench-collection-item:!opacity-100',
          titleOverlay && 'pointer-events-none absolute inset-x-0 z-10 px-3 py-2',
          placement === 'overlay-top' && 'top-0',
          placement === 'overlay-center' && 'top-1/2 -translate-y-1/2 motion-reduce:transform-none',
          placement === 'overlay-bottom' && 'bottom-0',
          surface === 'surface' && 'bg-background/90 backdrop-blur-sm',
          surface === 'scrim' && placement === 'overlay-top'
            && 'bg-gradient-to-b from-background/90 to-transparent pb-8',
          surface === 'scrim' && placement === 'overlay-bottom'
            && 'bg-gradient-to-t from-background/90 to-transparent pt-8',
          surface === 'scrim' && placement === 'overlay-center'
            && 'bg-background/75 backdrop-blur-sm',
          Boolean(maxLines) && 'overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical]',
          classNames?.itemTitle,
        )}
        style={maxLines ? { WebkitLineClamp: maxLines } : undefined}
      >
        {title}
      </div>
    ) : null;

    const actionsConfig = config.item?.actions;
    const actionsVisibility = actionsConfig?.visibility ?? 'always';
    const actions = actionsConfig && actionsVisibility !== 'never' ? context.renderSlot('actions') : null;
    const hasActions = Boolean(actionsConfig && hasRenderableNode(actions));
    const actionsPlacement = actionsConfig?.placement ?? 'overlay-bottom';
    const actionsOverlay = hasActions && actionsPlacement.startsWith('overlay-');
    const actionsNode = hasActions ? (
      <div
        data-collection-item-actions=""
        data-actions-placement={actionsPlacement}
        data-actions-align={actionsConfig?.align ?? 'center'}
        data-actions-visibility={actionsVisibility}
        className={cn(
          'flex min-w-0 items-center gap-2 px-2 py-2',
          actionsConfig?.align === 'start' && 'justify-start',
          (actionsConfig?.align === undefined || actionsConfig.align === 'center') && 'justify-center',
          actionsConfig?.align === 'end' && 'justify-end',
          actionsConfig?.wrap !== false && 'flex-wrap',
          actionsVisibility === 'hover'
            && '!opacity-0 transition-opacity group-hover/workbench-collection-item:!opacity-100 group-focus-within/workbench-collection-item:!opacity-100 [@media(hover:none)]:!opacity-100',
          actionsOverlay && 'pointer-events-none absolute inset-x-0 z-20',
          actionsPlacement === 'overlay-top' && 'top-1',
          actionsPlacement === 'overlay-bottom' && 'bottom-1',
          classNames?.itemActions,
        )}
      >
        <div className={cn('pointer-events-auto flex items-center gap-2', actionsConfig?.wrap !== false && 'flex-wrap')}>
          {actions}
        </div>
      </div>
    ) : null;

    if (!hasTitle && !hasActions) return content;

    return (
      <div
        className={cn('min-w-0', (titleOverlay || actionsOverlay) && 'relative', classNames?.itemContent)}
        data-collection-item-content=""
      >
        {placement === 'above' ? titleNode : null}
        {actionsPlacement === 'above' ? actionsNode : null}
        {content}
        {placement === 'below' || titleOverlay ? titleNode : null}
        {actionsPlacement === 'below' || actionsOverlay ? actionsNode : null}
      </div>
    );
  }, [classNames?.itemActions, classNames?.itemContent, classNames?.itemTitle, config.item, renderItem]);

  const renderEntry = useCallback((
    entry: NormalizedCollectionItem<Item>,
    layout: WorkbenchCollectionLayoutContext,
    includeShell = true,
  ) => {
    const context = createItemContext(entry, layout);
    const content = renderItemContent(entry, context);
    if (!includeShell) return content;
    return (
      <div {...getItemInteractionProps(entry, context)}>
        {content}
      </div>
    );
  }, [createItemContext, getItemInteractionProps, renderItemContent]);

  const baseLayoutContext = useMemo<WorkbenchCollectionLayoutContext>(() => ({
    mode,
    columnCount,
    columnWidth,
    containerWidth,
    gap,
    placementStrategy,
    rowHeight,
  }), [columnCount, columnWidth, containerWidth, gap, mode, placementStrategy, rowHeight]);

  const renderGroupItems = (group: NormalizedCollectionGroup<Item>) => {
    const virtualization = config.virtualization;
    const adapter = virtualization?.adapter;
    const virtualizationRequested = virtualization?.enabled === true;
    if (virtualizationRequested && (!adapter || adapter.supports?.(mode) === false)) {
      if (virtualization?.fallback === 'error') {
        return null;
      }
    }
    if (virtualizationRequested && adapter && adapter.supports?.(mode) !== false) {
      return adapter.render({
        items: group.items.map(({ item }) => item),
        keys: group.items.map(({ key }) => key),
        layout: config.layout,
        scrollOwner: resolveScrollOwner(),
        renderItem: (item, localIndex, layout) => {
          const entry = group.items[localIndex];
          return entry
            ? <Fragment key={entry.stringKey}>{renderEntry(entry, layout)}</Fragment>
            : null;
        },
      });
    }

    if (mode === 'masonry' || mode === 'dense') {
      return (
        <WorkbenchMasonryLayout
          items={group.items}
          columnCount={columnCount}
          columnWidth={config.layout.cardWidth ?? config.layout.columnWidth ?? config.layout.idealCardWidth}
          gap={gap}
          rowHeight={rowHeight}
          placementStrategy={mode === 'dense' ? 'dense' : placementStrategy}
          className={classNames?.layout}
          containerProps={{
            role: selectionMode === 'none' ? 'list' : 'listbox',
            'aria-label': ariaLabel ?? labels.collection,
            'aria-multiselectable': selectionMode === 'multiple' || undefined,
          }}
          getItemProps={(entry, masonryLayout) => {
            const context = createItemContext(entry, { ...masonryLayout, mode });
            return getItemInteractionProps(entry, context);
          }}
          getItemKey={(entry) => entry.key}
          getItemSize={(entry, masonryLayout) => {
            const layout: WorkbenchCollectionLayoutContext = {
              ...masonryLayout,
              mode,
            };
            const customSize = config.layout.getItemSize?.(entry.item, layout);
            const aspectRatio = config.layout.getItemAspectRatio?.(entry.item, entry.index);
            return normalizeItemSize(customSize, aspectRatio, masonryLayout.columnWidth);
          }}
          renderItem={(entry, masonryLayout: WorkbenchMasonryLayoutContext) => renderEntry(entry, {
            ...masonryLayout,
            mode,
          }, false)}
        />
      );
    }

    if (mode === 'rail') {
      const cardWidth = config.layout.cardWidth ?? config.layout.idealCardWidth ?? DEFAULT_CARD_WIDTH_PX;
      return (
        <div
          role={selectionMode === 'none' ? 'list' : 'listbox'}
          aria-label={ariaLabel ?? labels.collection}
          aria-multiselectable={selectionMode === 'multiple' || undefined}
          className={cn('grid min-w-0 grid-flow-col items-start overflow-x-auto', classNames?.layout)}
          style={{ gap, gridAutoColumns: typeof cardWidth === 'number' ? `${cardWidth}px` : cardWidth }}
        >
          {group.items.map((entry) => (
            <Fragment key={entry.stringKey}>{renderEntry(entry, baseLayoutContext)}</Fragment>
          ))}
        </div>
      );
    }

    return (
      <div
        role={selectionMode === 'none' ? 'list' : 'listbox'}
        aria-label={ariaLabel ?? labels.collection}
        aria-multiselectable={selectionMode === 'multiple' || undefined}
        className={cn('grid min-w-0 items-start', classNames?.layout)}
        style={{
          gap,
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        }}
      >
        {group.items.map((entry) => {
          const customSize = config.layout.getItemSize?.(entry.item, baseLayoutContext);
          const span = clamp(Math.round(Number(customSize?.columnSpan) || 1), 1, columnCount);
          const aspectRatio = config.layout.getItemAspectRatio?.(entry.item, entry.index);
          return (
            <div
              key={entry.stringKey}
              {...getItemInteractionProps(
                entry,
                createItemContext(entry, { ...baseLayoutContext, columnSpan: span, size: customSize }),
              )}
              style={{
                gridColumn: span > 1 ? `span ${span}` : undefined,
                aspectRatio: Number.isFinite(aspectRatio) && Number(aspectRatio) > 0
                  ? String(aspectRatio)
                  : undefined,
              }}
            >
              {renderEntry(entry, { ...baseLayoutContext, columnSpan: span, size: customSize }, false)}
            </div>
          );
        })}
      </div>
    );
  };

  const slotContext: WorkbenchCollectionSlotContext<Item> = {
    items,
    error: effectiveError,
    loading,
    loadingMore,
    selectedKeys,
    retry: () => callbacks?.onRetry?.(),
  };
  const header = resolveRenderableSlot(slots?.header, slotContext);
  const toolbar = resolveRenderableSlot(slots?.toolbar, slotContext);
  const selectionToolbar = selectedKeys.size > 0
    ? resolveRenderableSlot(slots?.selectionToolbar, slotContext)
    : null;
  const beforeItems = resolveRenderableSlot(slots?.beforeItems, slotContext);
  const footer = resolveRenderableSlot(slots?.footer, slotContext);
  const showPermission = state?.permission === true;
  const showOffline = state?.offline === true && !showPermission;
  const showInitialLoading = loading && items.length === 0 && !showPermission && !showOffline;
  const showInitialError = Boolean(effectiveError) && items.length === 0 && !showPermission && !showOffline && !showInitialLoading;
  const showEmpty = !showPermission && !showOffline && !showInitialLoading && !showInitialError && (state?.empty === true || items.length === 0);
  const loadingSlot = resolveRenderableSlot(slots?.loading, slotContext);
  const errorSlot = resolveRenderableSlot(slots?.error, slotContext);
  const permissionSlot = resolveRenderableSlot(slots?.permission, slotContext);
  const offlineSlot = resolveRenderableSlot(slots?.offline, slotContext);
  const emptySlot = resolveRenderableSlot(slots?.empty, slotContext);
  const loadMoreSlot = resolveRenderableSlot(slots?.loadMore, slotContext);
  const selfScrollable = config.layout.scrollOwner === 'self'
    || (config.layout.scrollOwner === undefined && (config.layout.fillHeight || config.layout.maxHeight));
  const rootStyle: CSSProperties = {
    ...style,
    padding: config.layout.padding ?? style?.padding,
    height: config.layout.fillHeight ? '100%' : style?.height,
    maxHeight: config.layout.maxHeight ?? style?.maxHeight,
    overflow: selfScrollable ? 'auto' : style?.overflow,
  };

  return (
    <section
      ref={rootRef}
      {...componentAttributes}
      data-monkeys-component="workbench-collection"
      data-layout-mode={mode}
      data-density={config.appearance?.density}
      data-radius={config.appearance?.radius}
      data-media-fit={config.appearance?.mediaFit}
      data-hover-effect={config.appearance?.hover}
      aria-label={ariaLabel ?? labels.collection}
      aria-busy={loading || loadingMore || undefined}
      className={cn('min-h-0 min-w-0', config.layout.fillHeight && 'h-full', classNames?.root, className)}
      style={rootStyle}
    >
      {hasRenderableNode(header) ? <div className={classNames?.header}>{header}</div> : null}
      {hasRenderableNode(toolbar) ? <div className={classNames?.toolbar}>{toolbar}</div> : null}
      {hasRenderableNode(selectionToolbar) ? (
        <div className={classNames?.selectionToolbar}>{selectionToolbar}</div>
      ) : null}
      {hasRenderableNode(beforeItems) ? <div className={classNames?.beforeItems}>{beforeItems}</div> : null}

      {showPermission ? (
        hasRenderableNode(permissionSlot) ? permissionSlot : (
          <BaseSystemState
            kind="permission"
            title={labels.permission}
            actions={statusStates.permission?.showRequestAccess !== false && callbacks?.onRequestAccess
              ? <BaseButton size="sm" label={labels.requestAccess} onClick={callbacks.onRequestAccess} />
              : undefined}
          />
        )
      ) : showOffline ? (
        hasRenderableNode(offlineSlot) ? offlineSlot : (
          <BaseSystemState
            kind="offline"
            title={labels.offline}
            actions={statusStates.error?.showRetry !== false && callbacks?.onRetry
              ? <BaseButton size="sm" label={labels.retry} onClick={callbacks.onRetry} />
              : undefined}
          />
        )
      ) : showInitialLoading ? (
        hasRenderableNode(loadingSlot) ? loadingSlot : loadingVariant === 'spinner' ? (
          <BaseLoadingState title={labels.loading} variant="spinner" />
        ) : (
          <div
            ref={registerItemsNode}
            className={cn('min-w-0', classNames?.items)}
          >
            <WorkbenchCollectionSkeleton
              mode={mode}
              phase="initial"
              columnCount={columnCount}
              columnWidth={columnWidth}
              cardWidth={config.layout.cardWidth ?? config.layout.idealCardWidth}
              gap={gap}
              rowHeight={rowHeight}
              config={config.loading?.skeleton}
              ariaLabel={labels.loading}
              className={classNames?.layout}
            />
          </div>
        )
      ) : showInitialError ? (
        hasRenderableNode(errorSlot) ? errorSlot : (
          <BaseSystemState
            kind="error"
            title={labels.loadError}
            actions={statusStates.error?.showRetry !== false && callbacks?.onRetry
              ? <BaseButton size="sm" label={labels.retry} onClick={callbacks.onRetry} />
              : undefined}
          />
        )
      ) : showEmpty ? (
        hasRenderableNode(emptySlot) ? emptySlot : <BaseEmptyState title={labels.empty} />
      ) : (
        <div
          ref={registerItemsNode}
          className={cn('min-w-0', classNames?.items)}
        >
          {groups.map((group, groupIndex) => {
            const groupContext: WorkbenchCollectionGroupContext<Item> = {
              key: group.key,
              label: group.label,
              items: group.items.map(({ item }) => item),
              groupIndex,
            };
            const heading = config.grouping
              ? slots?.groupHeading?.(groupContext, slotContext) ?? group.label
              : null;
            const headingId = `${componentId}-group-${groupIndex}`;
            return (
              <div
                key={group.stringKey}
                data-collection-group={group.stringKey}
                className={classNames?.group}
              >
                {hasRenderableNode(heading) ? (
                  <div id={headingId} role="heading" aria-level={2} className={classNames?.groupHeading}>
                    {heading}
                  </div>
                ) : null}
                {renderGroupItems(group)}
              </div>
            );
          })}
        </div>
      )}

      {items.length > 0 && effectiveError ? (
        <div className={classNames?.status}>
          {hasRenderableNode(errorSlot) ? errorSlot : (
            <BaseSystemState
              kind="error"
              title={labels.refreshError}
              actions={statusStates.error?.showRetry !== false && callbacks?.onRetry
                ? <BaseButton size="sm" label={labels.retry} onClick={callbacks.onRetry} />
                : undefined}
            />
          )}
        </div>
      ) : null}

      {items.length > 0 && (hasMore || loadingMore) ? (
        <div ref={loadMoreRef} className={classNames?.loadMore} data-collection-load-more="">
          {hasRenderableNode(loadMoreSlot)
            ? loadMoreSlot
            : loadingMore
              ? loadingVariant === 'spinner'
                ? <BaseLoadingState title={labels.loadingMore} variant="spinner" />
                : (
                    <WorkbenchCollectionSkeleton
                      mode={mode}
                      phase="load-more"
                      columnCount={columnCount}
                      columnWidth={columnWidth}
                      cardWidth={config.layout.cardWidth ?? config.layout.idealCardWidth}
                      gap={gap}
                      rowHeight={rowHeight}
                      config={config.loading?.skeleton}
                      ariaLabel={labels.loadingMore}
                      className={classNames?.layout}
                    />
                  )
              : null}
        </div>
      ) : null}
      {hasRenderableNode(footer) ? <div className={classNames?.footer}>{footer}</div> : null}
    </section>
  );
}
